import React, { useCallback, useRef } from "react";
import { supabase, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { invalidateAgentQueries } from "@/lib/invalidateAgentQueries";
import type { QueryClient } from "@tanstack/react-query";
import type { OrderRecapData } from "@/components/OrderRecapCard";
import {
  type VoiceAgentStatus,
  type VoiceMessage,
  type ConfirmationCardData,
  type ExecutionPlan,
  type OrderConfirmation,
  AGENT_THROTTLE_MS,
  QUICK_ACTION_THROTTLE_MS,
  withRetry,
  getProcessingMessageForIntent,
} from "@/hooks/voice-agent/types";

interface SendToAgentDeps {
  siteId: string | undefined;
  conversationId: string | null;
  messages: VoiceMessage[];
  queryClient: QueryClient;
  saveMessages: (msgs: VoiceMessage[]) => Promise<void>;
  speak: (text: string) => Promise<void>;
  playAudioMobileSafe: (url: string) => Promise<void>;
  setStatus: (s: VoiceAgentStatus) => void;
  setMessages: React.Dispatch<React.SetStateAction<VoiceMessage[]>>;
  setLastTranscription: (v: string | null) => void;
  setProcessingMessage: (v: string | null) => void;
  setConfirmationCard: (v: ConfirmationCardData | null) => void;
  setPendingOrderRecap: (v: OrderRecapData | null) => void;
  setPendingOrderInsertIndex: (v: number | null) => void;
  setPendingExecutionPlan: (v: ExecutionPlan | null) => void;
  setConfirmations: React.Dispatch<React.SetStateAction<OrderConfirmation[]>>;
  setConversationId: (v: string | null) => void;
}

export function useSendToAgent(deps: SendToAgentDeps) {
  const lastCallRef = useRef<number>(0);

  const sendToAgent = useCallback(async (text: string, isQuickAction: boolean = false) => {
    const {
      siteId, conversationId, messages, queryClient,
      saveMessages, speak, playAudioMobileSafe,
      setStatus, setMessages, setLastTranscription,
      setProcessingMessage, setConfirmationCard,
      setPendingOrderRecap, setPendingOrderInsertIndex,
      setPendingExecutionPlan, setConfirmations, setConversationId,
    } = deps;

    if (!siteId || !text.trim()) return;

    // Get a fresh token so the Edge Function gateway accepts the request (avoids 401 from expired or stale token)
    const { data: { session }, error: sessionError } = await supabase.auth.refreshSession();
    if (sessionError) {
      logger.warn("useVoiceAgent", "Session refresh failed", sessionError);
      toast.error("Session expirée", { description: "Reconnectez-vous pour utiliser l'assistant."});
      return;
    }
    if (!session?.access_token) {
      toast.error("Non connecté", { description: "Vous devez être connecté pour utiliser l'assistant."});
      return;
    }

    const projectRef = SUPABASE_URL.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] ?? "";
    try {
      const payload = JSON.parse(atob(session.access_token.split(".")[1] ?? "{}"));
      const tokenIssuer = (payload.iss as string) ?? "";
      if (projectRef && tokenIssuer && !tokenIssuer.includes(projectRef)) {
        logger.warn("useVoiceAgent", "Token issuer mismatch", { tokenIssuer, projectRef });
        toast.error("Session incohérente", { description: "Votre session ne correspond pas à cet environnement. Déconnectez-vous puis reconnectez-vous."});
        return;
      }
    } catch {
      // ignore JWT decode errors
    }

    // Throttling check - use faster throttle for quick actions
    const throttleMs = isQuickAction ? QUICK_ACTION_THROTTLE_MS : AGENT_THROTTLE_MS;
    const now = Date.now();
    if (now - lastCallRef.current < throttleMs) {
      logger.debug("useVoiceAgent", "Throttled - ignoring rapid call");
      return;
    }
    lastCallRef.current = now;

    setProcessingMessage("Analyse de votre demande...");
    setStatus("processing");

    // Add user message to display
    const userMessage: VoiceMessage = {
      role: "user",
      content: text.trim(),
      ts: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setLastTranscription(null); // Clear transcription once message is displayed

    const doVoiceAgentRequest = async (accessToken: string) => {
      const url = `${SUPABASE_URL}/functions/v1/voice-agent`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          siteId,
          textMessage: text.trim(),
          conversationId,
        }),
      });

      const resData = await res.json().catch((e) => {
        logger.error("useVoiceAgent", "Failed to parse response JSON:", e);
        return {};
      });
      if (!res.ok) {
        const msg =
          (resData as { error?: string; message?: string })?.error ??
          (resData as { error?: string; message?: string })?.message ??
          `Erreur ${res.status}`;
        const error = new Error(msg) as Error & { status?: number };
        error.status = res.status;
        throw error;
      }
      return resData;
    };

    const tryWithJwtRetry = async (): Promise<unknown> => {
      try {
        return await doVoiceAgentRequest(session.access_token);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const status = (err as Error & { status?: number }).status;
        const isJwtError = status === 401 || /invalid\s*jwt|jwt\s*invalid|unauthorized/i.test(msg);
        if (isJwtError) {
          const { data: { session: refreshed }, error: refErr } = await supabase.auth.refreshSession();
          if (!refErr && refreshed?.access_token) {
            logger.info("useVoiceAgent", "Retrying voice-agent after session refresh (JWT error)");
            return await doVoiceAgentRequest(refreshed.access_token);
          }
        }
        throw err;
      }
    };

    try {
      const data = await withRetry(tryWithJwtRetry);

      // Update processing message based on detected intent
      if (data?.intent) {
        setProcessingMessage(getProcessingMessageForIntent(data.intent));
      }

      // Handle confirmation card (for reservations and orders)
      if (data?.confirmationCard) {
        logger.info("useVoiceAgent", "Received confirmationCard", data.confirmationCard);
        setConfirmationCard(data.confirmationCard);

        // Add assistant message with flag to show confirmation card
        const assistantMessage: VoiceMessage = {
          role: "assistant",
          content: data.text || "",
          ts: new Date().toISOString(),
          showConfirmationCard: true,
          suggestedReplies: data.suggestedReplies,
        };

        setMessages(prev => [...prev, assistantMessage]);
        saveMessages([...messages, userMessage, assistantMessage]);

        // Invalidate queries ONLY when order is actually sent/completed
        // Not during intermediate steps (scheduled, awaiting_confirmation, etc.)
        const finalStatuses = ['order_sent', 'completed', 'preorder_confirmed'];
        if (finalStatuses.includes(data.confirmationCard.status)) {
          logger.info("useVoiceAgent", "Order finalized, invalidating queries", data.confirmationCard.status);
          invalidateAgentQueries(queryClient);

          // Clear pending order recap since order was confirmed
          setPendingOrderRecap(null);
          setPendingOrderInsertIndex(null);
        }

        // Use pre-generated audioUrl from backend if available (faster)
        // playAudioMobileSafe handles iOS/mobile autoplay restrictions and sets status to "speaking"
        // The audio callbacks handle setting status back to "idle" when done
        if (data.audioUrl) {
          playAudioMobileSafe(data.audioUrl).catch(err => {
            logger.warn("useVoiceAgent", "Audio playback failed", err);
            setStatus("idle"); // Only set idle on error
          });
          // Don't set status to idle here - playAudioMobileSafe sets it to "speaking" and handles "idle" on end
          return;
        } else if (data.text) {
          // Fallback: generate TTS client-side
          speak(data.text).catch(err => {
            logger.warn("useVoiceAgent", "TTS failed, continuing without audio", err);
            setStatus("idle"); // Only set idle on error
          });
          return;
        } else {
          // No audio to play - set idle immediately
          setStatus("idle");
          return;
        }
      }
      // Handle orderRecap (pending order visual card)
      else if (data?.orderRecap) {
        logger.info("useVoiceAgent", "Received orderRecap for pending order", data.orderRecap);
        setPendingOrderRecap(data.orderRecap);

        const assistantMessage: VoiceMessage = {
          role: "assistant",
          content: data.text || "",
          ts: new Date().toISOString(),
          suggestedReplies: data.suggestedReplies,
        };

        // Capture the index AFTER which the card should be displayed
        // This is the index of the assistant message we're about to add
        setMessages(prev => {
          const newMessages = [...prev, assistantMessage];
          setPendingOrderInsertIndex(newMessages.length); // Card shows after this message
          return newMessages;
        });
        saveMessages([...messages, userMessage, assistantMessage]);

        // Play audio - mobile-safe
        // playAudioMobileSafe sets status to "speaking" and handles "idle" on end
        if (data.audioUrl) {
          playAudioMobileSafe(data.audioUrl).catch(err => {
            logger.warn("useVoiceAgent", "Audio playback failed", err);
            setStatus("idle");
          });
          return; // Don't set idle below - audio callbacks handle it
        } else if (data.text) {
          speak(data.text).catch(err => {
            logger.warn("useVoiceAgent", "TTS failed, continuing without audio", err);
            setStatus("idle");
          });
          return; // Don't set idle below - speak callbacks handle it
        } else {
          setStatus("idle");
          return;
        }
      }
      // Handle text response (no confirmation card, no orderRecap)
      else if (data?.text) {
        // Only clear pending order recap if order was confirmed/cancelled (confirmationCard received)
        // Keep it visible during clarification flows
        if (data.confirmationCard) {
          setPendingOrderRecap(null);
        }

        const assistantMessage: VoiceMessage = {
          role: "assistant",
          content: data.text,
          ts: new Date().toISOString(),
          suggestedReplies: data.suggestedReplies,
          // Garder audioUrl sur le message : le son sera lancé au démarrage du typewriter (pas après)
          audioUrl: data.audioUrl,
        };

        setMessages(prev => [...prev, assistantMessage]);

        // Save conversation
        saveMessages([...messages, userMessage, assistantMessage]);

        // Ne pas jouer l'audio ici : il sera lancé par l'UI au onStart du typewriter (synchro voix + animation)
        if (!data.audioUrl) {
          speak(data.text).catch(err => {
            logger.warn("useVoiceAgent", "TTS failed, continuing without audio", err);
            setStatus("idle");
          });
        }
        return;
      }

      if (data?.executionPlan) {
        logger.info("useVoiceAgent", "Received ExecutionPlan", data.executionPlan);
        setPendingExecutionPlan(data.executionPlan);
      }

      // Handle confirmations (legacy support) - also triggers real-time update
      if (data?.confirmation) {
        setConfirmations(prev => [...prev, {
          type: data.confirmation.type,
          data: data.confirmation.data,
          ts: new Date().toISOString(),
        }]);

        // Invalidate relevant queries for immediate UI update
        invalidateAgentQueries(queryClient);
      }

      // Update conversation ID
      if (data?.conversationId && !conversationId) {
        setConversationId(data.conversationId);
      }

      setStatus("idle");

    } catch (error) {
      logger.error("useVoiceAgent", "Agent error after retries", error);
      setStatus("error");
      const msg = error instanceof Error ? error.message : "Une erreur est survenue";
      const isNetworkError = /failed to fetch|network error|load failed|connection/i.test(msg) || (error instanceof TypeError && msg.includes("fetch"));
      const isAuthError = !isNetworkError && (msg.includes("non-2xx") || msg.includes("401") || msg.toLowerCase().includes("unauthorized"));
      const description = isNetworkError
        ? "Connexion impossible. Vérifiez votre réseau ou réessayez."
        : isAuthError
          ? "Session expirée ou non reconnue. Reconnectez-vous puis réessayez."
          : msg;

      // Show error as a chat bubble instead of an ephemeral toast
      const errorMessage: VoiceMessage = {
        role: "assistant",
        content: description,
        ts: new Date().toISOString(),
        isError: true,
      };
      setMessages(prev => [...prev, errorMessage]);

      // Reset to idle after showing error
      setTimeout(() => setStatus("idle"), 2000);
    }
  }, [deps]);

  return sendToAgent;
}
